import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

interface User {
  id: number;
  name: string;
  phoneNumber: string;
  email?: string;
  role: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  login: (phoneNumber: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('adminToken');
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
      // Try to get user info from token
      try {
        const payload = JSON.parse(atob(storedToken.split('.')[1]));
        if (payload) {
          setUser({
            id: payload.userId || payload.id || 0,
            name: payload.name || 'Admin',
            phoneNumber: payload.phoneNumber || '',
            email: payload.email,
            role: payload.role || 'ADMIN',
          });
        }
      } catch (e) {
        console.warn('Could not decode user from token');
      }
    }
  }, []);

  const login = async (phoneNumber: string, password: string) => {
    try {
      // Use same login API as admin dashboard (mimicking admin login)
      const response = await api.post('/auth/login', {
        phoneNumber,
        password,
        role: 'ADMIN',
      });

      const { token: jwtToken, user: userData } = response.data;

      localStorage.setItem('adminToken', jwtToken);
      setToken(jwtToken);
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

